import { useState, useEffect, useRef } from "react";
import { getDistance } from "geolib";
import { evaluateDeliveryConfidence } from "../utils/photo-capture/confidence-utils";
import { resizeAndCompressImage } from "../utils/photo-capture/image-compress";
import { MAX_WIDTH, MAX_HEIGHT, JPEG_QUALITY } from "../utils/photo-capture/config.state";
import { 
  fetchScanAddress, 
  fetchOriginCoords, 
  updatePhotoData, 
  attachSignedUrlListener,
  saveImageToIdb,
  deleteImageFromIdb,
  uploadImageToSignedUrl
} from "../services/photoCaptureService";

const CONF_CODE = { High: "H", Medium: "M", Low: "L" };

export function usePhotoCapture(userID, uniqueKey, onDone, onPhotoDone) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanAddress, setScanAddress] = useState("");
  const [capturedImagePreview, setCapturedImagePreview] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [capturedLocation, setCapturedLocation] = useState(null);
  const [confidenceResult, setConfidenceResult] = useState(null);

  const streamRef = useRef(null);
  const signedUrlListenerRef = useRef(null);
  const lastCompressedRef = useRef(null);
  const lastFilenameRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchScanAddress(userID, uniqueKey).then(addr => {
      setScanAddress(addr);
      setLoading(false);
    });
    return () => {
      cleanupSignedUrlListener();
      stopCamera();
    };
  }, [userID, uniqueKey]);

  const isMobile = () => /Mobi|Android/i.test(navigator.userAgent || "");

  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("GPS_NOT_SUPPORTED"));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ x: pos.coords.latitude, y: pos.coords.longitude, acc: pos.coords.accuracy }),
        (err) => {
          if (err.code === 1) reject(new Error("GPS_DENIED"));
          else reject(new Error("GPS_UNAVAILABLE"));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const checkLocationConfidence = async () => {
    const origin = await fetchOriginCoords(userID, uniqueKey);
    const current = await getCurrentLocation();

    if (!origin || !isFinite(origin.x) || !isFinite(origin.y)) {
      return { photoCoords: current };
    }

    const dist = getDistance(
      { latitude: origin.x, longitude: origin.y },
      { latitude: current.x, longitude: current.y }
    );
    const coordAcc = isFinite(origin.acc) ? origin.acc : 999;
    const evaluation = evaluateDeliveryConfidence(dist, coordAcc, current.acc, origin.x, origin.y);

    return { photoCoords: current, distanceMetres: Math.round(dist), coordAcc: Math.round(coordAcc), evaluation };
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const openPicker = async () => {
    setStatus(null); setErrorMsg(null); setConfidenceResult(null);

    try {
      const locationResult = await checkLocationConfidence();
      setConfidenceResult(locationResult);
    } catch (err) {
      if (err.message === "GPS_DENIED") {
        setStatus("error");
        setErrorMsg("GPS / Location is turned off or blocked.\n\nPlease enable Location access.");
        return;
      }
      if (err.message === "GPS_NOT_SUPPORTED") {
        setStatus("error");
        setErrorMsg("Your device does not support GPS. Cannot take photo.");
        return;
      }
      console.warn("GPS unavailable:", err.message);
    }

    if (isMobile() || !navigator.mediaDevices?.getUserMedia) {
      triggerFileInput();
      return;
    }

    try {
      await openCameraInPage();
    } catch {
      triggerFileInput();
    }
  };

  async function openCameraInPage() {
    if (cameraOpen) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "environment" },
    });
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    }
    setCameraOpen(true);
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  }

  const captureFromCamera = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 720;
    const MAX_CAP = 1280;
    let cw = vw, ch = vh;
    if (vw > MAX_CAP || vh > MAX_CAP) {
      const asp = vw / vh;
      if (asp > 1) { cw = MAX_CAP; ch = Math.round(MAX_CAP / asp); }
      else { ch = MAX_CAP; cw = Math.round(MAX_CAP * asp); }
    }
    canvas.width = cw;
    canvas.height = ch;
    canvas.getContext("2d").drawImage(video, 0, 0, cw, ch);

    setCapturedImagePreview(canvas.toDataURL("image/jpeg", 0.8));

    try {
      setCapturedLocation(await getCurrentLocation());
    } catch (e) {
      if (confidenceResult?.photoCoords) setCapturedLocation(confidenceResult.photoCoords);
    }

    canvas.toBlob((blob) => {
      if (!blob) return;
      setCapturedFile(new File([blob], `${userID}_${uniqueKey}_${Date.now()}.jpg`, { type: "image/jpeg" }));
      stopCamera();
    }, "image/jpeg", 0.8);
  };

  const handleUploadPhoto = async () => {
    if (!capturedFile) return;
    setStatus("saving"); setErrorMsg(null);

    try {
      const compressed = await resizeAndCompressImage(capturedFile, MAX_WIDTH, MAX_HEIGHT, JPEG_QUALITY);
      const filename = `${userID}_${uniqueKey}_${Date.now()}.jpg`;
      await saveImageToIdb(filename, compressed);
      
      lastCompressedRef.current = compressed;
      lastFilenameRef.current = filename;

      const updatePayload = { requestToUpload: 1 };
      const photoLoc = capturedLocation || confidenceResult?.photoCoords;
      if (photoLoc) updatePayload.photoCoords = { x: photoLoc.x, y: photoLoc.y, acc: photoLoc.acc ?? null };

      if (confidenceResult?.evaluation) {
        const { level, distanceTier } = confidenceResult.evaluation;
        updatePayload.trustScore = {
          dist: confidenceResult.distanceMetres,
          conf: CONF_CODE[level] ?? level,
          ...(distanceTier === "high" ? { isFar: true } : {}),
        };
      }

      await updatePhotoData(userID, uniqueKey, updatePayload);
      setStatus("waiting-for-signed-url");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message);
      return;
    }

    cleanupSignedUrlListener();
    signedUrlListenerRef.current = attachSignedUrlListener(userID, uniqueKey, async (url) => {
      if (!lastCompressedRef.current) return;
      setStatus("uploading");
      await uploadImageToSignedUrl(url, lastCompressedRef.current);
      await updatePhotoData(userID, uniqueKey, { updateTimestamp: Date.now() });
      setStatus("uploaded");
      await deleteImageFromIdb(lastFilenameRef.current);
      setTimeout(callDoneCallback, 300);
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    setCapturedFile(file);
    try {
      setCapturedLocation(await getCurrentLocation());
    } catch (e) {
      if (confidenceResult?.photoCoords) setCapturedLocation(confidenceResult.photoCoords);
    }
    const reader = new FileReader();
    reader.onloadend = () => setCapturedImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  function cleanupSignedUrlListener() {
    if (signedUrlListenerRef.current) signedUrlListenerRef.current();
    signedUrlListenerRef.current = null;
  }

  const callDoneCallback = () => onPhotoDone?.() || onDone?.();
  const handleTryAgain = () => { setStatus(null); setErrorMsg(null); setConfidenceResult(null); };

  return {
    loading, status, errorMsg, cameraOpen, scanAddress, capturedImagePreview, confidenceResult,
    fileInputRef, videoRef, canvasRef,
    openPicker, captureFromCamera, stopCamera, handleUploadPhoto, handleFileSelect,
    callDoneCallback, handleTryAgain
  };
}
